/**
 * useWalletInflows — Task #12-FE, B6
 *
 * Ruft GET /api/v2/wallets/:id/inflows ab und liefert die Zeitreihe
 * der letzten 30 Tage fuer den InflowsChart.
 *
 * Konsistent mit useWalletDetail: gleicher apiClient, gleiche Query-Key-Konvention.
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type WalletInflowEntry = {
  date: string
  hnt: number
  iot: number
  mobile: number
}

export type WalletInflowsResponse = {
  data: WalletInflowEntry[]
}

// --- Fetch-Helper ---

async function fetchInflows(walletId: number): Promise<WalletInflowsResponse> {
  const result = await apiClient.GET('/api/v2/wallets/{id}/inflows', {
    params: { path: { id: walletId } },
  })
  if (result.error) throw result.error
  if (!result.data) {
    throw new Error('Query data is undefined for wallet inflows — expected error was not set by fetcher')
  }
  // Normalisiere optionale Felder: leere Eintraege werden zu 0-Werten
  const raw = result.data as { data?: Array<{ date?: string; hnt?: number; iot?: number; mobile?: number }> }
  const entries: WalletInflowEntry[] = (raw.data ?? []).map((entry) => ({
    date: entry.date ?? '',
    hnt: entry.hnt ?? 0,
    iot: entry.iot ?? 0,
    mobile: entry.mobile ?? 0,
  }))
  return { data: entries }
}

// --- Hook ---

type UseWalletInflowsResult = {
  inflows: WalletInflowEntry[] | undefined
  isLoading: boolean
  isError: boolean
}

export function useWalletInflows(walletId: number): UseWalletInflowsResult {
  const query = useQuery({
    queryKey: ['wallet', walletId, 'inflows'],
    queryFn: () => fetchInflows(walletId),
  })

  return {
    inflows: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
